import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {Popover} from "primeng/popover";
import {DunningData} from "../../../payment-plan/model/dunning-data.interface";
import {Charge} from "../../../payment-plan/model/charge.interface";
import {Contract} from "../../../payment-plan/model/contract.interface";
import {BmDebtCollectionService} from "../../service/bm-debt-collection.service";

@Component({
    selector: 'bm-debt-collection-activity',
    templateUrl: './bm-debt-collection-activity.component.html',
    styleUrls: ['./bm-debt-collection-activity.component.scss'],
    standalone: false
})
export class BmDebtCollectionActivityComponent implements OnInit, OnDestroy {

    dunningData: DunningData | null = null;

    // Charge adjustment dialog
    showChargeAdjustmentDialog: boolean = false;
    selectedCharge: Charge | null = null;
    chargeAdjustmentType: 'remove' | 'reduce' = 'remove';
    reducedChargeAmount: number = 0;

    selectedDate: Date = new Date();

    private destroy$ = new Subject<void>();
    selectedContractForPriceView: Contract;

    constructor(private debtCollectionService: BmDebtCollectionService) {
    }

    ngOnInit(): void {
        this.subscribeToServiceUpdates();
    }

    /**
     * Subscribe to service updates
     */
    private subscribeToServiceUpdates(): void {
        // Subscribe to dunning data
        this.debtCollectionService.dunningData$
            .pipe(takeUntil(this.destroy$))
            .subscribe(data => {
                this.dunningData = data;
                if (data) {
                    this.initializeMaxPaybackAmount();
                }
            });
    }

    /**
     * Initialize max payback amount from total charges and contract dunning amounts
     */
    private initializeMaxPaybackAmount(): void {
        if (!this.dunningData) return;

        let total = 0;

        // Add all charges
        this.dunningData.charges.forEach(charge => {
            total += charge.amount;
        });

        // Add all contract dunning amounts (initial debt)
        this.dunningData.contracts.forEach(contract => {
            if (contract.status === 'in-dunning') {
                total += contract.dunningAmount;
            }
        });
    }

    /**
     * Open charge adjustment dialog
     */
    openChargeAdjustmentDialog(charge: Charge): void {
        this.selectedCharge = charge;
        this.chargeAdjustmentType = 'remove';
        this.reducedChargeAmount = charge.amount;
        this.showChargeAdjustmentDialog = true;
    }

    /**
     * Apply charge adjustment
     */
    async applyChargeAdjustment(): Promise<void> {
        if (!this.selectedCharge) return;

        if (this.chargeAdjustmentType === 'remove') {
            await this.debtCollectionService.adjustCharge(this.selectedCharge, 'remove');
        } else if (this.chargeAdjustmentType === 'reduce') {
            await this.debtCollectionService.adjustCharge(
                this.selectedCharge,
                'reduce',
                this.reducedChargeAmount
            );
        }

        this.showChargeAdjustmentDialog = false;
    }

    /**
     * Check if charge is adjusted
     */
    isChargeAdjusted(charge: Charge): boolean {
        const adjustments = this.debtCollectionService['chargeAdjustmentsSubject'].value;
        return adjustments.some(adj => adj.charge.name === charge.name);
    }

    /**
     * Restore charge to original amount
     */
    async restoreCharge(charge: Charge): Promise<void> {
        await this.debtCollectionService.restoreCharge(charge);
    }

    getContractBadge(contract: Contract): 'STORNO' | 'WIK' | 'NEU' | 'NOT SELECTED' {
        switch (contract.status) {
            case 'new':
                return 'NEU';
            case 'in-dunning':
                return 'STORNO';
            case 'reactivated':
                return 'WIK';
            default:
                return 'NOT SELECTED';
        }
    }

    getContractStyleClass(contract: Contract): string {
        switch (contract.status) {
            case 'new':
                return 'bg-blue-100 text-blue-500';
            case 'in-dunning':
                return 'bg-orange-100 text-orange-500';
            case 'reactivated':
                return 'bg-green-100 text-green-500';
            default:
                return 'bg-red-100 text-red-500';
        }
    }

    // WIK date: derived UI helper (you don't have this date in the model)
    // We compute "today + (dunningMonths + reactivationMonths) months", first day of that month.
    getWikDate(contract: Contract): string {
        if (contract.status === 'new') {
            // for new contracts show "Startdatum" based on monthlyPrice startMonth
            const sm = contract.monthlyPrice?.[0]?.startMonth; // YYYY-MM
            return this.formatYearMonthToDate(sm);
        }

        const months = (contract.dunningMonths ?? 0) + (contract.reactivationMonths ?? 0);
        const d = new Date();
        d.setDate(1);
        d.setMonth(d.getMonth() + months);

        return this.formatDate(d);
    }

    private formatYearMonthToDate(ym?: string): string {
        // ym = "2026-01" → 01.01.2026
        if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return '';
        const [y, m] = ym.split('-').map(Number);
        const d = new Date(y, m - 1, 1);
        return this.formatDate(d);
    }

    private formatDate(d: Date): string {
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}.${mm}.${yyyy}`;
    }

    handleContractPriceView(pricePanel: Popover, event: MouseEvent, contract: Contract) {
        this.selectedContractForPriceView = contract;
        pricePanel.toggle(event);

    }

    toggleCharge(charge: Charge) {
        // Toggle is not Allowed for Active Charge with removable false
        if (!charge.deductable && this.isChargeActive(charge)) {
            return;
        }

        this.debtCollectionService.toggleCharge(charge);
    }

    isChargeActive(charge: Charge): boolean {
        return this.debtCollectionService.isChargeActive(charge.name);
    }

    updateContractDunningAmount(
        contract: Contract,
        operation: 'inc' | 'dec'
    ): void {
        this.debtCollectionService.changeContractMonths(
            contract.identifier,
            operation
        );
    }

    selectContract(contract: Contract): void {
        this.debtCollectionService.setSelectedContract(contract);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
